import React, { useEffect, useState } from 'react';
import { filterOptOutSects } from '../../components/utilities/digitalSurveyUtil';
import {
  DigitalSubmission,
  DigitalSubmissionMeta,
  QuestionCaseSetting,
} from '../../redux/types';
import { isAnswerEmpty } from '../../components/utilities/digitalQuestionUtil';

type SubmissionBySectionIdQuestionId = Record<number, Record<number, DigitalSubmission>>;
export type CompulsoryCtxSettingMapType = Record<number, QuestionCaseSetting | undefined>;

const checkCompulsoryContextSubmissions = (
  settingsArr: QuestionCaseSetting[],
  fsMap: SubmissionBySectionIdQuestionId,
) => {
  const pendingEntries = settingsArr
    .filter((s) => !s.digitalCaseId)
    .map((s) => {
      const submission = fsMap[s.digitalSectionId]?.[s.digitalQuestionId];
      if (isAnswerEmpty(submission) && s.compulsory) {
        return s;
      }
      return null;
    })
    .filter((s) => s !== null);
  return pendingEntries as QuestionCaseSetting[];
};

/**
 * The hook returns a dictionary; "`QuestionCaseSetting` by QuestionId"
 * of unanswered compulsory context questions. i.e. settings of those question
 * which are compulsory but their `DigitalSubmission` does not exist
 *
 * @param settings QuestionCaseSettings list
 * @param fullSubmissionMap DigitalSubmission dictionary by sectionId.QuestionId
 * @param dsms DigitalSubmissionMetas of the survey
 * @returns QuestionCaseSetting map by QuestionId and Pending SectionId
 */
const usePendingCompulsoryCtxQuestions = (
  settings: QuestionCaseSetting[],
  fullCtxSubmissionMap: SubmissionBySectionIdQuestionId,
  dsms?: DigitalSubmissionMeta[],
): [CompulsoryCtxSettingMapType?, number?] => {
  const [
    incompleteCtxSettingMap,
    setIncompleteCtxSettingMap,
  ] = useState<CompulsoryCtxSettingMapType>();
  const [pendingSecId, setPendingSecId] = useState<number>();

  useEffect(() => {
    const pendingCtxEntries = filterOptOutSects(
      checkCompulsoryContextSubmissions(settings, fullCtxSubmissionMap), dsms,
    );
    if (!pendingCtxEntries.length) {
      setIncompleteCtxSettingMap(undefined);
      setPendingSecId(undefined);
    } else {
      // set the first pending section id that it sees
      const pendingEntry = pendingCtxEntries[0];
      setPendingSecId(pendingEntry.digitalSectionId);
      const ctxSettingMap = pendingCtxEntries.reduce((acc, cur) => {
        acc[cur.digitalQuestionId] = cur;
        return acc;
      }, {} as Record<number, QuestionCaseSetting>);
      setIncompleteCtxSettingMap(pendingCtxEntries.length ? ctxSettingMap : undefined);
    }
  }, [settings, fullCtxSubmissionMap, dsms]);

  return [incompleteCtxSettingMap, pendingSecId];
};

export default usePendingCompulsoryCtxQuestions;
